import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { PublicAuthenticatedPhoneVerificationCard } from '@/components/public/visits/PublicAuthenticatedPhoneVerificationCard';
import { AGENT_ROUTE_PREFIX, isSecureAdminPath } from '@/constants/security';
import { useAuth } from '@/hooks/useAuth';
import { useAuthenticatedBookingReadiness } from '@/hooks/public/visits/useAuthenticatedBookingReadiness';
import {
  clearPublicAuthCompletionPromptTrigger,
  hasPublicAuthCompletionPromptTrigger,
  hasPublicAuthCompletionPromptSessionSeen,
  markPublicAuthCompletionPromptSessionSeen,
  readPublicAuthCompletionPromptState,
  writePublicAuthCompletionPromptState,
} from '@/lib/publicAuth';

const AUTH_BOUNDARY_PREFIXES = ['/login', '/register', '/request-password-reset', '/update-password', '/auth/callback'] as const;

function isEligiblePublicPromptPath(pathname: string): boolean {
  if (pathname === '/agendar/datos') {
    return false;
  }

  if (pathname.startsWith(AGENT_ROUTE_PREFIX) || isSecureAdminPath(pathname)) {
    return false;
  }

  return !AUTH_BOUNDARY_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function PublicAuthenticatedWhatsappSuggestion() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [shouldEvaluatePrompt, setShouldEvaluatePrompt] = useState(false);

  const eligiblePath = useMemo(() => isEligiblePublicPromptPath(location.pathname), [location.pathname]);
  const readinessQuery = useAuthenticatedBookingReadiness({
    enabled: isAuthenticated && eligiblePath && shouldEvaluatePrompt,
    userId: user?.id,
  });

  useEffect(() => {
    if (!isAuthenticated || !eligiblePath) {
      setOpen(false);
      setShouldEvaluatePrompt(false);
      return;
    }

    if (!user?.id) {
      setShouldEvaluatePrompt(false);
      return;
    }

    const persistedState = readPublicAuthCompletionPromptState(user.id);
    if (persistedState) {
      setOpen(false);
      setShouldEvaluatePrompt(false);
      clearPublicAuthCompletionPromptTrigger();
      markPublicAuthCompletionPromptSessionSeen(user.id);
      return;
    }

    const triggerPending = hasPublicAuthCompletionPromptTrigger();
    const sessionSeen = hasPublicAuthCompletionPromptSessionSeen(user.id);
    setShouldEvaluatePrompt(triggerPending || !sessionSeen);
  }, [eligiblePath, isAuthenticated, location.key, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !eligiblePath || !shouldEvaluatePrompt || !user?.id) {
      return;
    }

    const persistedState = readPublicAuthCompletionPromptState(user.id);
    if (persistedState) {
      clearPublicAuthCompletionPromptTrigger();
      markPublicAuthCompletionPromptSessionSeen(user.id);
      setShouldEvaluatePrompt(false);
      setOpen(false);
      return;
    }

    if (readinessQuery.isLoading) {
      return;
    }

    if (readinessQuery.isError) {
      clearPublicAuthCompletionPromptTrigger();
      markPublicAuthCompletionPromptSessionSeen(user.id);
      setShouldEvaluatePrompt(false);
      return;
    }

    const isPublicTenantActor = readinessQuery.data?.actorAccess.isPublicTenantActor ?? false;
    if (!isPublicTenantActor) {
      clearPublicAuthCompletionPromptTrigger();
      markPublicAuthCompletionPromptSessionSeen(user.id);
      setShouldEvaluatePrompt(false);
      setOpen(false);
      return;
    }

    const requiresVerifiedPhone = readinessQuery.data?.missingRequirements.includes('verified_phone_required') ?? false;
    clearPublicAuthCompletionPromptTrigger();
    markPublicAuthCompletionPromptSessionSeen(user.id);
    setShouldEvaluatePrompt(false);
    setOpen(requiresVerifiedPhone);
  }, [eligiblePath, isAuthenticated, readinessQuery.data, readinessQuery.isError, readinessQuery.isLoading, shouldEvaluatePrompt, user?.id]);

  if (!isAuthenticated || !user?.id || !eligiblePath) {
    return null;
  }

  const handleDismiss = () => {
    writePublicAuthCompletionPromptState(user.id, 'dismissed');
    markPublicAuthCompletionPromptSessionSeen(user.id);
    clearPublicAuthCompletionPromptTrigger();
    setShouldEvaluatePrompt(false);
    setOpen(false);
  };

  const handleVerified = async () => {
    writePublicAuthCompletionPromptState(user.id, 'completed');
    markPublicAuthCompletionPromptSessionSeen(user.id);
    clearPublicAuthCompletionPromptTrigger();
    setShouldEvaluatePrompt(false);
    setOpen(false);
    await readinessQuery.refetch();
  };

  return open ? (
    <PublicAuthenticatedPhoneVerificationCard
      userId={user.id}
      onVerified={handleVerified}
      showSummaryCard={false}
      autoOpen={open}
      dismissible
      onDismiss={handleDismiss}
      title="Verifica tu WhatsApp una sola vez"
      description="Así protegemos mejor tu cuenta y tus próximas visitas podrán avanzar mucho más fácil."
      successDescription="Tu cuenta quedó lista para que las próximas visitas fluyan sin volver a pedir esta validación."
      secondaryAction={{
        label: 'Ahora no',
        onClick: handleDismiss,
      }}
    />
  ) : null;
}